// eslint-disable-next-line
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Text,
  Panel,
  Link,
  theme,
  Flex,
  Progress,
  Toast,
  Spinner,
  Flow,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { centsToDollars, numberMoneyFormatter } from '../../../utils/helpers';
import { useQuery } from '@apollo/client';
import { HasUserSubmittedApplicationQuery } from '../../../schema/generated/schema';
import TooltipBlueIcon from '../../../components/TooltipBlueIcon';
import { Link as ReactLink } from 'react-router-dom';
import { useGetTargetUserData } from '../../../contexts/User/UserContext';
import { NotificationContext } from '../../../contexts/Notification';
import { initiateResendEmailVerification } from '../../User/UserDisplay/helpers';

const billingProfileEdit = css`
  float: right;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
`;

const billingProfileEditLink = css`
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const BillingProfile = ({ index, profile, isStaff, userID, onEditClick }) => {
  const { contact } = profile;
  const [hasUserSubmittedApplication, setHasUserSubmittedApplication] =
    useState(false);
  const { userData, isLoading, error } = useGetTargetUserData();
  const [verifiedEmailAddress, setVerifiedEmailAddress] = useState(false);
  const [isHandlingClick, setIsHandlingClick] = useState(false);
  const [successVerificationEmailSent, setSuccessfulVerificationEmailSent] =
    useState(false);
  const { addNotification } = useContext(NotificationContext);

  useEffect(() => {
    if (!isLoading && !error) {
      const { email_address_verified } = userData;
      setVerifiedEmailAddress(email_address_verified);
    }
  }, [isLoading]);

  useQuery(HasUserSubmittedApplicationQuery, {
    variables: {
      billingAccountId: profile.id,
    },
    onCompleted: (data) => {
      setHasUserSubmittedApplication(
        data.hasUserSubmittedApplication.isSubmitted
      );
    },
  });

  const handleVerifyEmailClick = async () => {
    setIsHandlingClick(true);
    const { notificationType, notificationMessage } =
      await initiateResendEmailVerification(userID, userData.email_address);
    if (notificationType === 'success') {
      setSuccessfulVerificationEmailSent(true);
    }
    addNotification(notificationType, notificationMessage);
    setIsHandlingClick(false);
  };

  if (isLoading || error) return <Spinner />;

  return (
    <>
      <Flex>
        {/* Billing Account Info Panel */}
        <Panel width="100%" padding={theme.space[3]}>
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={theme.space[3]}
          >
            <Text kind="body-title">Accounts Payable</Text>
            {profile.createdBy === userID && (
              <div css={billingProfileEdit}>
                <Link
                  onClick={() => onEditClick(index)}
                  data-billing-profile-index={index}
                  css={billingProfileEditLink}
                >
                  Edit
                </Link>
              </div>
            )}
          </Flex>
          {contact && (
            <Flex flexWrap="wrap" flexDirection="row">
              <Box flexBasis="100%">
                <Text
                  kind="body-sm-title"
                  fontWeight={theme.fontWeights.semibold}
                  marginBottom={theme.space[1]}
                >
                  Billing Contact
                </Text>
                <Text
                  kind="body-sm"
                  className="pii-encrypt"
                  display="block"
                  marginBottom={theme.space[1]}
                >
                  {contact.name}
                </Text>
                <Text
                  kind="body-sm"
                  className="pii-encrypt"
                  display="block"
                  marginBottom={theme.space[1]}
                >
                  {contact.phoneNumber}
                </Text>
                <Flex marginTop={theme.space[1]}>
                  <Text
                    kind="body-sm"
                    className="pii-encrypt"
                    display="block"
                    marginBottom={theme.space[2]}
                  >
                    Accounts Payable Email
                  </Text>
                  <TooltipBlueIcon bottom="100%" alignment="center">
                    Your Accounts Payable email address is{' '}
                    {contact.emailAddress}
                  </TooltipBlueIcon>
                </Flex>
              </Box>
              {contact.address && (
                <Box flexBasis="100%">
                  <Text
                    kind="body-sm-title"
                    fontWeight={theme.fontWeights.semibold}
                    marginBottom={theme.space[1]}
                  >
                    Billing Address
                  </Text>
                  <Text
                    kind="body-sm"
                    className="pii-encrypt"
                    display="block"
                    marginBottom={theme.space[1]}
                  >
                    {contact.address.street1}
                  </Text>
                  {contact.address.street2 && (
                    <Text
                      kind="body-sm"
                      className="pii-encrypt"
                      display="block"
                      marginBottom={theme.space[1]}
                    >
                      {contact.address.street2}
                    </Text>
                  )}
                  <Text
                    kind="body-sm"
                    className="pii-encrypt"
                    display="block"
                    marginBottom={theme.space[1]}
                  >
                    {`${contact.address.city}, ${contact.address.state} ${contact.address.zipcode}`}
                  </Text>
                </Box>
              )}
            </Flex>
          )}
        </Panel>

        {/* Xometry Terms Panel */}
        {/* User has not signed up for terms */}
        {!hasUserSubmittedApplication &&
          (profile.status === 'SUSPENDED' ||
            profile.status === 'ACTIVE' ||
            profile.status === 'PENDING') &&
          Boolean(!profile.creditLimitInCents) && (
            <Panel
              width="100%"
              padding={theme.space[3]}
              marginLeft={theme.space[4]}
            >
              {!verifiedEmailAddress && (
                <Toast kind="warning" isOpen={true} hideClose={true}>
                  {successVerificationEmailSent
                    ? 'Check your email to get verified.'
                    : 'Send the verification email to access the form.'}
                </Toast>
              )}
              <Text
                kind="body-title"
                marginBottom={theme.space[2]}
                display="block"
              >
                Set Up Terms With Xometry
              </Text>
              <Text kind="body-sm" display="block">
                Check out with purchase orders on Xometry by filling out the
                Terms Setup Form. Your organization’s EIN/Tax ID is required and
                {verifiedEmailAddress
                  ? ' financial documentation may be necessary.'
                  : ' your email will need to be verified to access the form.'}
              </Text>
              <Flex
                marginTop={theme.space[5]}
                justifyContent="center"
                width={1}
              >
                {verifiedEmailAddress ? (
                  <ReactLink
                    style={{ width: '100%' }}
                    to={{
                      pathname: `/account/apply-for-terms${
                        isStaff ? `/${userID}` : ''
                      }`,
                    }}
                    state={{ billingAccount: profile }}
                  >
                    <Button
                      kind={theme.kind.primary}
                      width={1}
                      onClick={() => {
                        if (window.analytics) {
                          window.analytics.track(
                            'Apply for Credit CTA Clicked',
                            {
                              staffCreated: isStaff ? 'Yes' : 'No',
                            }
                          );
                        }
                      }}
                    >
                      Get Started
                    </Button>
                  </ReactLink>
                ) : (
                  <Button
                    kind={theme.kind.primary}
                    width={1}
                    onClick={handleVerifyEmailClick}
                  >
                    {isHandlingClick ? (
                      <Flow
                        kind="disabled"
                        scale="panel"
                        padding={theme.space[2]}
                      />
                    ) : (
                      <>
                        {successVerificationEmailSent
                          ? 'Resend Verification Email'
                          : 'Send Verification Email'}
                      </>
                    )}
                  </Button>
                )}
              </Flex>
            </Panel>
          )}
        {/* User has pending or approved terms */}
        {(hasUserSubmittedApplication ||
          (profile.status === 'ACTIVE' &&
            Boolean(profile.creditLimitInCents))) && (
          <Panel
            width="100%"
            padding={theme.space[3]}
            marginLeft={theme.space[4]}
          >
            <Text
              kind="body-sm-title"
              marginBottom={theme.space[1]}
              display="block"
            >
              Credit Limit
            </Text>
            {profile.creditLimitInCents ? (
              <Text>{centsToDollars(profile.creditLimitInCents)}</Text>
            ) : (
              <Text>Pending Limit</Text>
            )}
            {profile.creditTerm && (
              <Flex marginTop={theme.space[1]}>
                <Text kind="body-sm" className="pii-encrypt">
                  {profile.creditTerm}
                </Text>
                <TooltipBlueIcon>
                  These are the agreed upon terms. Invoices are sent to the
                  email listed for Accounts Payable {profile.creditTerm} days
                  from when your order has been shipped.
                </TooltipBlueIcon>
              </Flex>
            )}
            <Flex marginTop={theme.space[2]}>
              <Text
                kind="body-sm-title"
                marginBottom={theme.space[1]}
                display="block"
              >
                Available Credit Balance
              </Text>
              <TooltipBlueIcon>
                Your Available Credit Balance represents the remaining credit
                that can be used for purchase orders by your organization.
              </TooltipBlueIcon>
            </Flex>
            <Text
              className="pii-encrypt"
              kind="page-title"
              fontWeight={theme.fontWeights.semibold}
              color={theme.colors.greens[125]}
              display="block"
              marginBottom={theme.space[1]}
            >
              {(profile.creditLimitInCents &&
                `${centsToDollars(profile.availableCreditInCents)}`) ||
                'Balance Pending'}
            </Text>
            <Progress
              percentage={
                (profile.availableCreditInCents / profile.creditLimitInCents) *
                100
              }
              scale="small"
              width="fill-available"
            />
            <Flex justifyContent="space-between">
              {profile.creditLimitInCents ? (
                <Text kind="body-sm">
                  ${numberMoneyFormatter(profile.creditLimitInCents / 100)}{' '}
                  Limit
                </Text>
              ) : (
                <Text kind="body-sm">Pending Limit</Text>
              )}
              {profile.availableCreditInCents && (
                <Text kind="body-sm">
                  ${numberMoneyFormatter(profile.availableCreditInCents / 100)}{' '}
                  Remaining
                </Text>
              )}
            </Flex>
          </Panel>
        )}
      </Flex>
    </>
  );
};

export default BillingProfile;
